/**
 * Streaming Markdown Renderer Component
 * Renders markdown progressively during streaming with optimized performance
 * Uses React.memo and proper key management to minimize re-render cost
 */
import { memo } from 'react';
import MarkdownRenderer from './MarkdownRenderer';

interface StreamingMarkdownRendererProps {
  content: string;
  isStreaming?: boolean;
  conversationTitle?: string;
  onExportTable?: (tableMarkdown: string, title: string) => void;
}

// Memoized wrapper to prevent unnecessary re-renders of MarkdownRenderer
// Only re-renders when content actually changes
const MemoizedMarkdownRenderer = memo(
  MarkdownRenderer,
  (prev, next) => prev.content === next.content && prev.conversationTitle === next.conversationTitle && prev.onExportTable === next.onExportTable
);

function StreamingMarkdownRenderer({
  content,
  isStreaming = false, // eslint-disable-line @typescript-eslint/no-unused-vars
  conversationTitle,
  onExportTable
}: StreamingMarkdownRendererProps) {
  // Always render markdown, both during streaming and after completion
  // ReactMarkdown handles incomplete markdown gracefully and preserves whitespace
  // The memoization prevents expensive re-renders when content hasn't changed
  // Note: isStreaming parameter kept for API compatibility but not needed for logic

  if (!content) {
    return null;
  }

  return (
    <MemoizedMarkdownRenderer
      content={content}
      conversationTitle={conversationTitle}
      onExportTable={onExportTable}
    />
  );
}

// Export with memo to prevent parent re-renders from causing unnecessary updates
export default memo(StreamingMarkdownRenderer);
